import type { CloudVault } from '../vault/cloudVault';
import { isConflictSibling } from '../plan/board';

export { dayLabel, monthLabel } from '../design/dates';

/**
 * Everything that has been captured, newest first, from paths alone.
 *
 * The board can read its whole set — 91 arcs. This surface is over ~4,900
 * files, so nothing here opens one. A capture's path carries its date and its
 * kind, which is enough to order, count and group the entire corpus before a
 * single byte is decrypted; content arrives later, per visible row.
 */

export type ThoughtKind = 'recording' | 'note' | 'chat' | 'other';

export interface CorpusItem {
  path: string;
  name: string;
  kind: ThoughtKind;
  /** Capture time from the filename. Null for the handful of files in a year
   *  folder that were named by hand rather than by the recorder. */
  at: Date | null;
}

const CAPTURED = /^(Recording|Note|Space|Chat) (\d{4})-(\d{2})-(\d{2}) at (\d{2})-(\d{2})/;

/** A year folder holds captures; everything else in the vault is structure
 *  (`arcs/`, `_markers/`, `threads/`) and does not belong in a chronology. */
const IN_A_YEAR_FOLDER = /^\d{4}\//;

export function buildCorpus(vault: CloudVault): CorpusItem[] {
  const items = vault
    .listPaths('')
    .filter((path) => IN_A_YEAR_FOLDER.test(path) && path.endsWith('.md'))
    .filter((path) => !isConflictSibling(path))
    .map(itemFor)
    // Chats live in the month folders as their own files, but they are not
    // captures: the app's feeds never surface them top-level (a chat is
    // reached through the note that holds it — its Members reference), and
    // this chronology mirrors that. A hand-NAMED chat is not recognisable
    // from its path alone and stays listed, which is fine — naming one is a
    // deliberate act.
    .filter((item) => item.kind !== 'chat');

  // Undated files sort last rather than to 1970: they are captures whose name
  // was replaced by hand, not the oldest things in the vault.
  return items.sort((a, b) => {
    if (a.at && b.at) return b.at.getTime() - a.at.getTime();
    if (a.at) return -1;
    if (b.at) return 1;
    return a.path.localeCompare(b.path);
  });
}

export function itemFor(path: string): CorpusItem {
  const name = path.replace(/^.*\//, '');
  const m = name.match(CAPTURED);
  if (!m) return { path, name, kind: 'other', at: null };

  const [, word, y, mo, d, hh, mm] = m;
  return {
    path,
    name,
    kind: word === 'Recording' ? 'recording' : word === 'Note' ? 'note' : word === 'Chat' ? 'chat' : 'other',
    at: new Date(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mm)),
  };
}

export function timeOf(item: CorpusItem): string {
  if (!item.at) return '';
  return `${String(item.at.getHours()).padStart(2, '0')}:${String(item.at.getMinutes()).padStart(2, '0')}`;
}
